import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { FileText, Eye, CheckSquare, Square, FileEdit, Trash2, PauseCircle, XCircle, CheckCircle } from 'lucide-react';
import RichTextEditor from '../components/Editor/Editor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGES_CONFIG = {
  'Stage 1': {
    name: 'Directions for Submitting Claim: Timelines and Procedure Established',
    substages: []
  },
  'Stage 2': {
    name: 'Claim and IA (if any) Submitted: Directions for Reply',
    substages: [
      { id: 'Stage 2A', name: 'Reply Not Submitted: Interim Order (IO) Passed, Last Opportunity for Reply' },
      { id: 'Stage 2B', name: 'Reply Not Submitted: Statement of Defence (SOD) Closed, Ex-Parte Issues Framed, Directions for Claimant’s Evidence' },
      { id: 'Stage 2C', name: 'Claimant Evidence Submitted: Proceedings Closed and Reserved for Award' },
      { id: 'Stage 2D', name: 'Ex-Parte Award' }
    ]
  },
  'Stage 3': {
    name: 'Reply Submitted: IO Passed, Directions for Rejoinder',
    substages: [
      { id: 'Stage 3A', name: 'Rejoinder Filed: Issues Framed, Directions for Claimant’s Evidence' },
      { id: 'Stage 3B', name: 'Rejoinder Not Filed: Issues Framed, Directions for Claimant’s Evidence' },
      { id: 'Stage 3C', name: 'Claimant Evidence Submitted: Directions for Respondent’s Evidence' },
      { id: 'Stage 3D', name: 'Respondent Evidence Submitted: Proceedings Closed, Reserved for Award' },
      { id: 'Stage 3E', name: 'Respondent Evidence Not Submitted: Proceedings Closed, Reserved for Award' },
      { id: 'Stage 3F', name: 'Final Award – Claim Allowed' },
      { id: 'Stage 3G', name: 'Final Award – Claim Rejected' },
      { id: 'Stage 3H', name: 'Settlement Award' },
      { id: 'Stage 3I', name: 'Withdrawal of Case' }
    ]
  },
  'Stage 4': {
    name: 'Objection Petition Filed: Direction for Reply',
    substages: [
      { id: 'Stage 4A', name: 'Reply Filed: Direction for Rejoinder' },
      { id: 'Stage 4B', name: 'Rejoinder Submitted: Direction for Oral Hearing' },
      { id: 'Stage 4C', name: 'Oral Hearing Conducted: Order Reserved on Objections' },
      { id: 'Stage 4D', name: 'Objection Rejected: Proceedings Restored at the Stage of Filing Objections' },
      { id: 'Stage 4E', name: 'Objection Accepted: Proceedings Terminated' }
    ]
  },
  'Stage 5': {
    name: 'IA Submitted: Direction for Reply',
    substages: [
      { id: 'Stage 5A', name: 'Reply Not Submitted: IO Passed, Proceedings Restored at the Stage of Filing IA' },
      { id: 'Stage 5B', name: 'Reply Submitted: Direction for Rejoinder' },
      { id: 'Stage 5C', name: 'Rejoinder Not Submitted: IO Passed, Proceedings Restored at the Stage of Filing IA' },
      { id: 'Stage 5D', name: 'Rejoinder Submitted: IO Passed, Proceedings Restored at the Stage of Filing IA' }
    ]
  },
  'Stage 6': {
    name: 'Amendment Application in Claim Filed: Direction for Reply',
    substages: [
      { id: 'Stage 6A', name: 'Reply Not Filed: Application Allowed, Amended Claim Kept on Record, Proceedings Restored at the Stage of Filing AA' },
      { id: 'Stage 6B', name: 'Reply Filed: Direction for Rejoinder' },
      { id: 'Stage 6C', name: 'Rejoinder Not Filed: Application Allowed, Amended Claim Kept on Record, Proceedings Restored at the Stage of Filing AA' },
      { id: 'Stage 6D', name: 'Rejoinder Filed: Application Allowed, Amended Claim Kept on Record, Proceedings Restored at the Stage of Filing AA' },
      { id: 'Stage 6E', name: 'Amendment Application Rejected: Proceedings Restored at the Stage of Filing AA' }
    ]
  },
  'Stage 7': {
    name: 'Oral Hearing',
    substages: [
      { id: 'Stage 7A', name: 'Oral Hearing Scheduled' },
      { id: 'Stage 7B', name: 'Oral Hearing Conducted' }
    ]
  }
};

const Cases = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [claimants, setClaimants] = useState([]);
  const [arbitrators, setArbitrators] = useState([]);
  const [ars, setArs] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [filters, setFilters] = useState({
    stage: '',
    claimant_id: '',
    ar_id: '',
    arbitrator_id: ''
  });
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [stageData, setStageData] = useState({
    new_stage: 'Stage 0',
    arbitrator_id: '',
    template_id: '',
    comment: '',
    next_hearing_date: ''
  });
  const [loading, setLoading] = useState(true);
  const [selectedCases, setSelectedCases] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMode, setBulkMode] = useState('stage'); // 'stage' or 'document'
  const [bulkData, setBulkData] = useState({
    main_stage: '',
    new_stage: 'Stage 0',
    arbitrator_id: '',
    notice_type: '',
    template_id: '',
    content: '',
    comment: '',
    next_hearing_date: '',
    hearing_time: ''
  });
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchCases();
    if (user.role === 'admin' || user.role === 'tribunal_secretary') {
      fetchDropdowns();
    }
  }, [filters, activeTab]);

  const fetchCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Add status filter based on active tab
      if (activeTab !== 'all') {
        params.append('status_filter', activeTab);
      }
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const res = await axios.get(`${API_URL}/cases?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(res.data.cases);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [claimantsRes, arbitratorsRes, arsRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/claimants`, { headers }),
        axios.get(`${API_URL}/admin/arbitrators`, { headers }),
        axios.get(`${API_URL}/admin/authorized-representatives`, { headers }),
        axios.get(`${API_URL}/notices/templates`, { headers })
      ]);

      setClaimants(claimantsRes.data.claimants);
      setArbitrators(arbitratorsRes.data.arbitrators);
      setArs(arsRes.data.authorized_representatives);
      setTemplates(templatesRes.data.templates);
    } catch (err) {
      console.error('Failed to fetch dropdowns:', err);
    }
  };

  const handleMarkStage = (caseItem) => {
    setSelectedCase(caseItem);
    setShowStageModal(true);
  };

  const handleUpdateStage = async () => {
    try {
      const token = localStorage.getItem('token');
      
      let stageName = stageData.new_stage || stageData.main_stage;
      const mainStageObj = STAGES_CONFIG[stageData.main_stage];
      
      if (activeTab === 'new') {
        stageName = 'Initiation and Invitation of Arbitration';
      } else if (mainStageObj) {
        if (stageData.new_stage) {
          const sub = mainStageObj.substages.find(s => s.id === stageData.new_stage);
          stageName = sub ? sub.name : stageData.new_stage;
        } else {
          stageName = mainStageObj.name;
        }
      }

      const payload = { 
        case_id: selectedCase._id, 
        ...stageData,
        new_stage: activeTab === 'new' ? 'Stage 0' : (stageData.new_stage || stageData.main_stage),
        stage_name: stageName
      };
      
      await axios.post(
        `${API_URL}/cases/mark-stage`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowStageModal(false);
      fetchCases();
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const handleAcceptCase = async (caseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/cases/arbitrator/accept-case/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCases();
    } catch (err) {
      console.error('Failed to accept case:', err);
    }
  };

  const handleRejectCase = async (caseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/cases/arbitrator/reject-case/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCases();
    } catch (err) {
      console.error('Failed to reject case:', err);
    }
  };

  const handleSpecialAction = async (action) => {
    const comment = prompt(`Enter reason for bulk ${action}:`);
    if (comment === null) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/cases/special-action`,
        { case_ids: selectedCases, action, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCases([]);
      fetchCases();
    } catch (err) {
      console.error(`Failed to bulk ${action} cases:`, err);
      alert(err.response?.data?.detail || `Failed to bulk ${action} cases`);
    }
  };

  const handleSelectCase = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId) 
        : [...prev, caseId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCases.length === cases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(cases.map(c => c._id));
    }
  };

  const handleBulkSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (bulkMode === 'stage') {
        let stageName = bulkData.new_stage || bulkData.main_stage;
        const mainStageObj = STAGES_CONFIG[bulkData.main_stage];
        
        if (activeTab === 'new') {
          stageName = 'Initiation and Invitation of Arbitration';
        } else if (mainStageObj) {
          if (bulkData.new_stage) {
            const sub = mainStageObj.substages.find(s => s.id === bulkData.new_stage);
            stageName = sub ? sub.name : bulkData.new_stage;
          } else {
            stageName = mainStageObj.name;
          }
        }

        await axios.post(`${API_URL}/cases/mark-stage-bulk`, {
          case_ids: selectedCases,
          ...bulkData,
          new_stage: activeTab === 'new' ? 'Stage 0' : (bulkData.new_stage || bulkData.main_stage),
          stage_name: stageName
        }, { headers });
      } else {
        await axios.post(`${API_URL}/notices/generate-bulk`, {
          case_ids: selectedCases,
          html: bulkData.content,
          template_id: bulkData.template_id,
          notice_type: bulkData.notice_type
        }, { headers });
      }

      setShowBulkModal(false);
      setSelectedCases([]);
      fetchCases();
    } catch (err) {
      console.error('Bulk action failed:', err);
      alert(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t._id === templateId);
    if (template) {
      setBulkData({
        ...bulkData,
        template_id: templateId,
        content: template.content,
        notice_type: template.notice_type
      });
    } else {
      setBulkData({ ...bulkData, template_id: '', content: '' });
    }
  };

  const noticeTypes = [...new Set(templates.map(t => t.notice_type))];
  const filteredTemplates = bulkData.notice_type 
    ? templates.filter(t => t.notice_type === bulkData.notice_type)
    : [];

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Cases</h1>
          <p className="page-description">View and manage all cases</p>
        </div>
        {(user.role === 'admin' || user.role === 'tribunal_secretary') && selectedCases.length > 0 && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {activeTab !== 'pending_approval' && (
              <button 
                className="btn-primary"
                onClick={() => {
                  setBulkMode('stage');
                  if (activeTab === 'new') {
                    setBulkData(prev => ({ ...prev, new_stage: 'Stage 0' }));
                  }
                  setShowBulkModal(true);
                }}
              >
                {activeTab === 'new' ? `Move to Invitation (${selectedCases.length})` : `Change Stage (${selectedCases.length})`}
              </button>
            )}
            
            <button 
              className="btn-secondary"
              onClick={() => {
                setBulkMode('document');
                setShowBulkModal(true);
              }}
              title="Bulk Generate Documents"
            >
              Generate Documents
            </button>

            <button 
              className="action-btn-bulk" 
              style={{ padding: '0.5rem 1rem', background: '#ecc94b', color: 'white', border: 'none', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
              onClick={() => handleSpecialAction('hold')} 
              title="Hold Selected Cases"
            >
              <PauseCircle size={18} /> Hold
            </button>

            <button 
              className="action-btn-bulk" 
              style={{ padding: '0.5rem 1rem', background: '#f56565', color: 'white', border: 'none', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
              onClick={() => handleSpecialAction('withdraw')} 
              title="Withdraw Selected Cases"
            >
              <XCircle size={18} /> Withdraw
            </button>

            <button 
              className="action-btn-bulk" 
              style={{ padding: '0.5rem 1rem', background: '#4299e1', color: 'white', border: 'none', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
              onClick={() => handleSpecialAction('dispose')} 
              title="Dispose Selected Cases"
            >
              <CheckCircle size={18} /> Dispose
            </button>
          </div>
        )}
      </div>

      {/* Status Tabs */}
      <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: '0.5rem', whiteSpace: 'nowrap' }}>
          {[
            { id: 'new', label: 'New Cases' },
            { id: 'pending_approval', label: 'Pending for Approval' },
            { id: 'arbitrator_appointed', label: 'Arbitrator Appoint' },
            { id: 'closed', label: 'Closed' }
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent',
                color: activeTab === tab.id ? '#667eea' : '#718096',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

        {(user.role === 'admin' || user.role === 'tribunal_secretary' || user.role === 'authorized_representative') && (
          <div className="filter-bar" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>Claimant</label>
              <select
                className="filter-select"
                style={{ width: '100%' }}
                value={filters.claimant_id}
                onChange={(e) => setFilters({ ...filters, claimant_id: e.target.value })}
              >
                <option value="">All Claimants</option>
                {claimants.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#718096', marginBottom: '0.25rem' }}>AR</label>
              <select
                className="filter-select"
                style={{ width: '100%' }}
                value={filters.ar_id}
                onChange={(e) => setFilters({ ...filters, ar_id: e.target.value })}
              >
                <option value="">All ARs</option>
                {ars.map((a) => (
                  <option key={a._id} value={a._id}>{a.name}</option>
                ))}
              </select>
            </div>

            <div style={{ 
              flex: '1', 
              minWidth: '200px',
              border: activeTab === 'pending_approval' ? '2px solid #667eea' : 'none',
              borderRadius: '8px',
              padding: activeTab === 'pending_approval' ? '2px' : '0'
            }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.75rem', 
                color: activeTab === 'pending_approval' ? '#667eea' : '#718096', 
                fontWeight: activeTab === 'pending_approval' ? 700 : 400,
                marginBottom: '0.25rem' 
              }}>
                Arbitrator {activeTab === 'pending_approval' && '(Required for Approval Check)'}
              </label>
              <select
                data-testid="arbitrator-filter"
                className="filter-select"
                style={{ width: '100%' }}
                value={filters.arbitrator_id}
                onChange={(e) => setFilters({ ...filters, arbitrator_id: e.target.value })}
              >
                <option value="">All Arbitrators</option>
                {arbitrators.map((a) => (
                  <option key={a._id} value={a._id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="card">
          {loading ? (
            <div>Loading...</div>
          ) : cases.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <div className="empty-state-text">No cases found</div>
            </div>
          ) : (
            <table data-testid="cases-table" className="data-table">
              <thead>
                <tr>
                  {user.role === 'admin' && (
                    <th style={{ width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedCases.length === cases.length && cases.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                  )}
                  <th>Case Number</th>
                  <th>Agreement No</th>
                  <th>Batch Name</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c._id} className={selectedCases.includes(c._id) ? 'selected-row' : ''}>
                    {user.role === 'admin' && (
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedCases.includes(c._id)}
                          onChange={() => handleSelectCase(c._id)}
                        />
                      </td>
                    )}
                    <td>{c.case_number || 'Not assigned'}</td>
                    <td>{c.agreement_no}</td>
                    <td>{c.batch_name}</td>
                    <td>
                      {c.stage_name || c.stage}
                      {c.previous_stage && (
                        <div style={{ fontSize: '0.75rem', color: '#718096', fontStyle: 'italic' }}>
                          From: {c.previous_stage}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${c.stage === 'new' ? 'new' : c.stage.includes('Stage') ? 'ongoing' : 'closed'}`}>
                        {c.stage === 'new' ? 'New' : c.stage.includes('Stage') ? 'Ongoing' : 'Closed'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          data-testid="view-repository"
                          className="action-btn"
                          onClick={() => navigate(`/case-repository/${c._id}`)}
                          title="View Repository"
                        >
                          <Eye size={18} color="#4A5568" />
                        </button>
                        
                        {(user.role === 'admin' || user.role === 'tribunal_secretary') && activeTab === 'new' && (
                          <button 
                            className="action-btn" 
                            onClick={() => {
                              setSelectedCase(c);
                              setStageData({ ...stageData, new_stage: 'Stage 0' });
                              setShowStageModal(true);
                            }}
                            title="Move to Invitation"
                          >
                            <FileEdit size={18} color="#667eea" />
                          </button>
                        )}
                        
                        {(user.role === 'admin' || user.role === 'tribunal_secretary') && activeTab === 'arbitrator_appointed' && (
                          <button 
                            className="action-btn" 
                            onClick={() => handleMarkStage(c)} 
                            title="Change Stage"
                          >
                            <CheckSquare size={18} color="#48bb78" />
                          </button>
                        )}
                        
                        {user.role === 'arbitrator' && c.arbitrator_status === 'pending' && (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              data-testid="accept-case-button"
                              className="btn-success-small"
                              onClick={() => handleAcceptCase(c._id)}
                            >
                              Accept
                            </button>
                            <button
                              data-testid="reject-case-button"
                              className="btn-danger-small"
                              onClick={() => handleRejectCase(c._id)}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {showStageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0 }}>Stage Movement</h2>
            <p style={{ color: '#718096', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Case: <strong>{selectedCase?.case_number || selectedCase?.agreement_no}</strong>
            </p>

            {activeTab !== 'new' ? (
              <>
                <div className="form-group">
                  <label>Main Stage</label>
                  <select
                    className="form-select"
                    value={stageData.main_stage}
                    onChange={(e) => setStageData({ ...stageData, main_stage: e.target.value, new_stage: '' })}
                  >
                    <option value="">Select Main Stage</option>
                    {Object.keys(STAGES_CONFIG).map(key => (
                      <option key={key} value={key}>{key} - {STAGES_CONFIG[key].name}</option>
                    ))}
                  </select>
                </div>

                {stageData.main_stage && STAGES_CONFIG[stageData.main_stage].substages.length > 0 && (
                  <div className="form-group">
                    <label>Sub Stage</label>
                    <select
                      className="form-select"
                      value={stageData.new_stage}
                      onChange={(e) => setStageData({ ...stageData, new_stage: e.target.value })}
                    >
                      <option value="">Select Sub Stage</option>
                      {STAGES_CONFIG[stageData.main_stage].substages.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.id} - {sub.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Hearing Date & Time for Stage 7A */}
                {(stageData.new_stage === 'Stage 7A') && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Hearing Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={stageData.next_hearing_date}
                        onChange={(e) => setStageData({ ...stageData, next_hearing_date: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Hearing Time</label>
                      <input
                        type="time"
                        className="form-input"
                        value={stageData.hearing_time}
                        onChange={(e) => setStageData({ ...stageData, hearing_time: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {stageData.new_stage === 'Stage 0' && (
              <div className="form-group">
                <label>Select Arbitrator</label>
                <select
                  data-testid="arbitrator-select"
                  className="form-select"
                  value={stageData.arbitrator_id}
                  onChange={(e) => setStageData({ ...stageData, arbitrator_id: e.target.value })}
                >
                  <option value="">Select an arbitrator</option>
                  {arbitrators.map((a) => (
                    <option key={a._id} value={a._id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Select Notice Template to Generate</label>
              <select
                data-testid="template-select"
                className="form-select"
                value={stageData.template_id}
                onChange={(e) => setStageData({ ...stageData, template_id: e.target.value })}
              >
                <option value="">No notice generation</option>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>{t.template_name} ({t.notice_type})</option>
                ))}
              </select>
              <small style={{ color: '#718096', display: 'block', marginTop: '0.5rem' }}>
                Selected template will be auto-generated for this case
              </small>
            </div>

            <div className="form-group">
              <label>Comment / Notes</label>
              <textarea
                data-testid="comment-input"
                className="form-input"
                rows="3"
                value={stageData.comment}
                onChange={(e) => setStageData({ ...stageData, comment: e.target.value })}
                placeholder="Add any notes or comments for this stage movement..."
              />
            </div>

            <div className="form-group">
              <label>Next Hearing Date (Optional)</label>
              <input
                data-testid="hearing-date-input"
                type="datetime-local"
                className="form-input"
                value={stageData.next_hearing_date}
                onChange={(e) => setStageData({ ...stageData, next_hearing_date: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                data-testid="confirm-stage-button"
                className="btn-primary"
                onClick={handleUpdateStage}
              >
                Change Stage
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowStageModal(false);
                  setStageData({ new_stage: 'Stage 0', arbitrator_id: '', template_id: '', comment: '', next_hearing_date: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: bulkMode === 'document' ? '900px' : '600px', width: '90%' }}>
            <h2 style={{ marginTop: 0 }}>
              {bulkMode === 'stage' ? 'Bulk Stage Movement' : 'Bulk Document Generation'}
            </h2>
            <p style={{ color: '#718096', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Selected Cases: <strong>{selectedCases.length}</strong>
            </p>

            {bulkMode === 'stage' ? (
              <>
                  {activeTab !== 'new' && (
                    <>
                      <div className="form-group">
                        <label>Main Stage</label>
                        <select
                          className="form-select"
                          value={bulkData.main_stage}
                          onChange={(e) => setBulkData({ ...bulkData, main_stage: e.target.value, new_stage: '' })}
                        >
                          <option value="">Select Main Stage</option>
                          {Object.keys(STAGES_CONFIG).map(key => (
                            <option key={key} value={key}>{key} - {STAGES_CONFIG[key].name}</option>
                          ))}
                        </select>
                      </div>

                      {bulkData.main_stage && STAGES_CONFIG[bulkData.main_stage].substages.length > 0 && (
                        <div className="form-group">
                          <label>Sub Stage</label>
                          <select
                            className="form-select"
                            value={bulkData.new_stage}
                            onChange={(e) => setBulkData({ ...bulkData, new_stage: e.target.value })}
                          >
                            <option value="">Select Sub Stage</option>
                            {STAGES_CONFIG[bulkData.main_stage].substages.map(sub => (
                              <option key={sub.id} value={sub.id}>{sub.id} - {sub.name}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Hearing Date & Time for Stage 7A */}
                      {(bulkData.new_stage === 'Stage 7A') && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div className="form-group">
                            <label>Hearing Date</label>
                            <input
                              type="date"
                              className="form-input"
                              value={bulkData.next_hearing_date}
                              onChange={(e) => setBulkData({ ...bulkData, next_hearing_date: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Hearing Time</label>
                            <input
                              type="time"
                              className="form-input"
                              value={bulkData.hearing_time}
                              onChange={(e) => setBulkData({ ...bulkData, hearing_time: e.target.value })}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {(bulkData.new_stage === 'Stage 0' || activeTab === 'new') && (
                  <div className="form-group">
                    <label>Select Arbitrator</label>
                    <select
                      className="form-select"
                      value={bulkData.arbitrator_id}
                      onChange={(e) => setBulkData({ ...bulkData, arbitrator_id: e.target.value })}
                    >
                      <option value="">Select an arbitrator</option>
                      {arbitrators.map((a) => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Comment / Notes</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={bulkData.comment}
                    onChange={(e) => setBulkData({ ...bulkData, comment: e.target.value })}
                    placeholder="Add notes for this bulk action..."
                  />
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Notice Type</label>
                    <select
                      className="form-select"
                      value={bulkData.notice_type}
                      onChange={(e) => setBulkData({ ...bulkData, notice_type: e.target.value, template_id: '', content: '' })}
                    >
                      <option value="">Select Notice Type</option>
                      {noticeTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Select Template</label>
                    <select
                      className="form-select"
                      value={bulkData.template_id}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      disabled={!bulkData.notice_type}
                    >
                      <option value="">Select Template</option>
                      {filteredTemplates.map(t => (
                        <option key={t._id} value={t._id}>{t.template_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {bulkData.template_id && (
                  <div className="form-group">
                    <label>Edit Content for Bulk Generation</label>
                    <div style={{ minHeight: '300px', marginBottom: '1rem' }}>
                      <RichTextEditor
                        value={bulkData.content}
                        onChange={(content) => setBulkData({ ...bulkData, content })}
                        height={400}
                      />
                    </div>
                    <small style={{ color: '#718096' }}>
                      Note: Document will be generated with these edits for all selected cases.
                    </small>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                className="btn-primary"
                onClick={handleBulkSubmit}
                disabled={bulkMode === 'document' && !bulkData.template_id}
              >
                {bulkMode === 'stage' ? 'Change Stage' : 'Generate Documents'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkData({ ...bulkData, template_id: '', content: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-content {
          background: white;
          borderRadius: 12px;
          padding: 2rem;
          maxHeight: 90vh;
          overflowY: auto;
        }
        .selected-row {
          background-color: #f7fafc;
        }
      `}</style>
    </MainLayout>
  );
};
export default Cases;
